<script setup lang="ts">
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAnalytics } from "@/composables/useAnalytics";
import AppIcon from "./AppIcon.vue";

const { t } = useI18n();
const { trackNavigationClick } = useAnalytics();

const toggleNav = ref<HTMLInputElement | null>(null);
const router = useRouter();
const route = useRoute();

// Close menu when route changes
watch(
  () => route.fullPath,
  () => {
    closeMenu();
  },
);

const localePath = useLocalePath();

const closeMenu = () => {
  if (toggleNav.value) {
    toggleNav.value.checked = false;
  }
};

const scrollToElement = (hash: string) => {
  const elementId = hash.replace("#", "");
  const el = document.getElementById(elementId);
  if (el) {
    const headerOffset = 110; // Align with router.options.ts
    const elementPosition = el.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

const handleAnchorClick = async (hash: string) => {
  closeMenu();

  const currentPath = route.path;
  const homePath = localePath("/");

  const isHome = currentPath === homePath || currentPath === homePath + "/";

  if (!isHome) {
    await router.push({ path: homePath, hash: hash });
  } else {
    scrollToElement(hash);
    // Optional: update URL hash manually if needed, but avoid if it causes issues.
    // history.pushState(null, "", hash);
  }
};

const generateAnchorLink = (hash: string) => {
  const homePath = localePath("/");
  const isHome = route.path === homePath || route.path === homePath + "/";
  return isHome ? hash : `${homePath}${hash}`;
};

const { user, otpSent, authError, login, verifyOtp } = useAuth();
const showAuthModal = ref(false);
const email = ref("");
const otpInput = ref("");

const handleAuth = async () => {
  const success = await login(email.value);
};

const handleVerifyOtp = async () => {
  if (await verifyOtp(email.value, otpInput.value)) {
    showAuthModal.value = false;
    navigateTo(localePath("/xtranet"));
  }
};

watch(showAuthModal, (val) => {
  if (!val) {
    email.value = "";
    otpInput.value = "";
  }
});

// Computed property for logo link - always goes to home
const logoLink = computed(() => localePath("/"));
</script>

<template>
  <header
    id="app-header"
    class="sticky top-0 z-50 bg-white dark:bg-gray-900 transition-all duration-300"
  >
    <nav class="w-full">
      <Container>
        <div
          class="flex flex-wrap items-center justify-between py-2 md:py-4 gap-6 md:gap-0 relative"
        >
          <input
            aria-hidden="true"
            type="checkbox"
            name="toggle_nav"
            id="toggle_nav"
            class="hidden peer"
            ref="toggleNav"
          />
          <div
            class="relative z-20 w-full flex items-center justify-between lg:w-max md:px-0"
          >
            <NuxtLink
              :to="logoLink"
              aria-label="logo"
              class="flex space-x-2 items-center"
              @click="trackNavigationClick('logo', logoLink)"
            >
              <AppIcon width="170" @click="closeMenu" />
            </NuxtLink>

            <div class="relative flex items-center lg:hidden max-h-10">
              <label
                role="button"
                for="toggle_nav"
                aria-label="humburger"
                id="hamburger"
                class="relative p-6 -mr-6"
              >
                <div
                  aria-hidden="true"
                  id="line"
                  class="m-auto h-0.5 w-5 rounded bg-sky-900 transition duration-300"
                ></div>
                <div
                  aria-hidden="true"
                  id="line2"
                  class="m-auto mt-2 h-0.5 w-5 rounded bg-sky-900 transition duration-300"
                ></div>
              </label>
            </div>
          </div>

          <div
            aria-hidden="true"
            class="fixed z-10 inset-0 h-screen w-screen bg-white/70 backdrop-blur-2xl origin-bottom scale-y-0 transition duration-500 peer-checked:origin-top peer-checked:scale-y-100 lg:hidden"
            @click="closeMenu"
          ></div>

          <div
            class="flex-col z-20 flex-wrap gap-6 p-8 rounded-3xl border border-gray-100 bg-white shadow-2xl shadow-gray-600/10 justify-end w-full invisible opacity-0 translate-y-1 absolute top-full left-0 transition-all duration-300 scale-95 origin-top lg:relative lg:scale-100 lg:peer-checked:translate-y-0 lg:translate-y-0 lg:flex lg:flex-row lg:items-center lg:gap-0 lg:p-0 lg:bg-transparent lg:w-fit lg:ps-6 lg:visible lg:opacity-100 lg:border-none peer-checked:scale-100 peer-checked:opacity-100 peer-checked:visible lg:shadow-none"
          >
            <div
              class="text-gray-600 dark:text-gray-300 lg:pr-4 lg:w-auto w-full lg:pt-0"
            >
              <ul
                class="tracking-wide font-medium lg:text-sm flex-col flex lg:flex-row gap-6 lg:gap-0"
              >
                <!-- Features -->
                <li>
                  <NuxtLink
                    :to="generateAnchorLink('#features')"
                    @click.prevent="() => handleAnchorClick('#features')"
                    class="block md:px-4 transition hover:text-primary"
                  >
                    {{ t("spendzen.menu.features") }}
                  </NuxtLink>
                </li>

                <!-- How it works -->
                <li>
                  <NuxtLink
                    :to="generateAnchorLink('#how-it-works')"
                    @click.prevent="() => handleAnchorClick('#how-it-works')"
                    class="block md:px-4 transition hover:text-primary"
                  >
                    {{ t("spendzen.menu.how_it_works") }}
                  </NuxtLink>
                </li>

                <!-- Pricing -->
                <li>
                  <NuxtLink
                    :to="generateAnchorLink('#pricing')"
                    @click.prevent="() => handleAnchorClick('#pricing')"
                    class="block md:px-4 transition hover:text-primary"
                  >
                    {{ t("spendzen.menu.pricing") }}
                  </NuxtLink>
                </li>

                <!-- FAQ -->
                <li>
                  <NuxtLink
                    :to="generateAnchorLink('#faq')"
                    @click.prevent="() => handleAnchorClick('#faq')"
                    class="block md:px-4 transition hover:text-primary"
                  >
                    {{ t("spendzen.menu.faq") }}
                  </NuxtLink>
                </li>
              </ul>
            </div>

            <div
              class="mt-4 lg:mt-0 flex flex-col sm:flex-row gap-4 sm:gap-2 items-center"
            >
              <!-- Language and Region Switch -->
              <div class="flex items-center gap-1">
                <ThemeSwitcher />
                <RegionLanguageSwitch />
              </div>

              <div
                class="mt-12 lg:mt-0 flex flex-col items-center gap-4 lg:flex-row"
              >
                <AppButton
                  @click="showAuthModal = true"
                  variant="primary"
                  size="sm"
                  :text="t('header.login')"
                  icon="ph:user-bold"
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </nav>
  </header>

  <!-- MODALE D'AUTHENTIFICATION -->
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="showAuthModal"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-md"
        @click.self="showAuthModal = false"
      >
        <div
          class="bg-white dark:bg-gray-900 p-8 md:p-10 rounded-[2.5rem] shadow-2xl w-full max-w-[400px] border border-gray-100 dark:border-gray-800 relative overflow-hidden"
        >
          <!-- Background Decor -->
          <div
            class="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"
          ></div>
          <div
            class="absolute -bottom-24 -left-24 w-48 h-48 bg-secondary/10 rounded-full blur-3xl"
          ></div>

          <div class="relative z-10">
            <h3
              class="font-black text-3xl text-gray-900 dark:text-white mb-8 tracking-tight"
            >
              {{ t("header.login_modal.title") }}
            </h3>

            <template v-if="!otpSent">
              <div class="space-y-6">
                <div class="space-y-2">
                  <label
                    class="text-sm font-bold text-gray-700 dark:text-gray-300 ml-1"
                  >
                    {{ t("header.login_modal.email_label") }}
                  </label>
                  <input
                    v-model="email"
                    type="email"
                    :placeholder="t('header.login_modal.email_placeholder')"
                    class="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary dark:text-white transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    @keyup.enter="handleAuth"
                    autocomplete="email"
                  />
                </div>

                <p
                  v-if="authError"
                  class="text-red-500 text-sm font-medium animate-shake"
                >
                  {{ authError }}
                </p>

                <AppButton
                  @click="handleAuth"
                  variant="primary"
                  size="lg"
                  class="w-full justify-center py-4 rounded-2xl shadow-xl shadow-primary/20"
                  :text="t('header.login_modal.submit')"
                  :disabled="!email"
                />
              </div>
            </template>

            <template v-else>
              <div class="space-y-6 text-center">
                <div class="p-4 bg-primary/10 rounded-2xl">
                  <p
                    class="text-primary dark:text-primary text-sm font-semibold"
                  >
                    {{ t("header.login_modal.success_message") }}
                  </p>
                </div>

                <div class="space-y-2">
                  <label
                    class="text-sm font-bold text-gray-700 dark:text-gray-300 block text-left ml-1"
                  >
                    Code de vérification
                  </label>
                  <input
                    v-model="otpInput"
                    type="text"
                    placeholder="000000"
                    class="w-full bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-2xl px-5 py-4 text-center text-2xl tracking-[0.5em] font-black focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary dark:text-white transition-all"
                    @keyup.enter="handleVerifyOtp"
                  />
                </div>

                <p v-if="authError" class="text-red-500 text-sm font-medium">
                  {{ authError }}
                </p>

                <div class="flex flex-col gap-3 pt-2">
                  <AppButton
                    @click="handleVerifyOtp"
                    variant="primary"
                    size="lg"
                    class="w-full justify-center py-4 rounded-2xl shadow-xl shadow-primary/20"
                    text="Se connecter"
                  />
                  <button
                    @click="showAuthModal = false"
                    class="text-gray-500 dark:text-gray-400 text-sm font-bold hover:text-gray-700 dark:hover:text-white transition-colors"
                  >
                    {{ t("header.login_modal.close") }}
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style>
#toggle_nav:checked ~ div #hamburger #line {
  @apply rotate-45 translate-y-1.5;
}
#toggle_nav:checked ~ div #hamburger #line2 {
  @apply -rotate-45 -translate-y-1;
}
</style>
